import { ReferenceDirection } from '@ardoq/api-types';
import { ChartWindowState, Size } from '@ardoq/graph';
import { WILDCARD } from '@ardoq/common-helpers';
import {
  BubbleChartGridMode,
  BubbleChartViewSettings,
  FieldSource,
  Quadrant,
  QuadrantInfo,
} from './types';
import { viewSettingsConsts } from '@ardoq/view-settings';
import { Point } from '@ardoq/math';

export const DISPLAYED_LABELS_LIMIT = 100;

export const BUBBLE_SLIDER_OPTIONS = { min: 0, max: 1, step: 0.01 };
export const BUBBLE_SLIDER_RANGE = { min: 1 / 10, max: 4 };

export const CONTEXT_HIGHLIGHT_STROKE_WIDTH = 4;

export const ZERO_BUBBLE = 'zeroBubble';
export const ZERO_BUBBLE_SIZE = 6;

export const DEFAULT_BUBBLE_RADIUS = 12;

export const DEFAULT_BUBBLE_CHART_VIEW_SETTINGS = {
  selectedFieldNameX: '',
  selectedFieldNameY: '',
  selectedFieldNameRadius: '',
  background: BubbleChartGridMode.NONE,
  isLegendActive: false,
  customQuadrantSettings: {
    xMinimum: NaN,
    xMaximum: NaN,
    yMinimum: NaN,
    yMaximum: NaN,
    labels: ['one', 'two', 'three', 'four'],
  },
  bubbleAreaScaleFactor: 0.5,
  fieldSource: FieldSource.BY_COMPONENT,
  includeAllDescendants: false,
  selectedReferenceTypeName: null,
  selectedReferenceDirection: ReferenceDirection.INCOMING,
  valueSourceComponentTypes: null,
  showBubblesWithZeroValue: true,
  selectedComponentTypes: [WILDCARD],
  selectedQuadrant: null,
} satisfies BubbleChartViewSettings;

export const EMPTY_HIGHLIGHT_STATE = {
  target: null,
  id: '',
  labelPosition: null,
};

export const EMPTY_CLIENT_RECT: Size = { width: 0, height: 0 };

export const DOMAIN_RELEVANT_VIEW_SETTINGS = [
  'background',
  'customQuadrantSettings',
] as const satisfies (keyof BubbleChartViewSettings)[];

/**
 * The filter function ignores the global settings (currently 'isLegendActive') implicitly.
 * But we add it for the reference within the view model, so that we can pick the view settings
 * ignored in the rendering data from the unfiltered settings, thereby avoiding outdated states.
 */
export const VIEW_SETTINGS_IGNORED_IN_BUBBLE_CHART_VIEW_MODEL_SOURCE: (keyof BubbleChartViewSettings)[] =
  [
    ...DOMAIN_RELEVANT_VIEW_SETTINGS,
    viewSettingsConsts.IS_LEGEND_ACTIVE,
  ] as const satisfies (keyof BubbleChartViewSettings)[];

export const QUADRANT_INFO_MAP: Record<Quadrant, QuadrantInfo> = {
  [Quadrant.TOP_LEFT]: {
    xDirection: 1,
    yDirection: -1,
    centerOffsetX: -0.25,
    centerOffsetY: -0.25,
  },
  [Quadrant.TOP_RIGHT]: {
    xDirection: -1,
    yDirection: -1,
    centerOffsetX: 0.25,
    centerOffsetY: -0.25,
  },
  [Quadrant.BOTTOM_LEFT]: {
    xDirection: 1,
    yDirection: 1,
    centerOffsetX: -0.25,
    centerOffsetY: 0.25,
  },
  [Quadrant.BOTTOM_RIGHT]: {
    xDirection: -1,
    yDirection: 1,
    centerOffsetX: 0.25,
    centerOffsetY: 0.25,
  },
} as const;

export const TITLE_MARGIN = 5;
export const DEFAULT_WINDOW_CENTER: Point = [0.5, 0.5];
export const DEFAULT_CHART_WINDOW_STATE: ChartWindowState = {
  windowCenter: DEFAULT_WINDOW_CENTER,
  windowScale: 1.0,
};
